/**
 * Spot WebSocket API
 *
 * Access market data, manage accounts, and trade on Binance Spot.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { WebsocketAPIBase, WebsocketApiResponse, WebsocketSendMsgOptions } from '@binance/common';
import type {
    AccountCommissionResponse,
    AccountRateLimitsOrdersResponse,
    AccountStatusResponse,
    AllOrderListsResponse,
    AllOrdersResponse,
    MyAllocationsResponse,
    MyFiltersResponse,
    MyPreventedMatchesResponse,
    MyTradesResponse,
    OpenOrderListsStatusResponse,
    OpenOrdersStatusResponse,
    OrderAmendmentsResponse,
    OrderListStatusResponse,
    OrderStatusResponse,
} from '../types';

/**
 * AccountApi - interface
 *
 * @interface AccountApi
 */
export interface AccountApiInterface {
    /**
     * Get current account commission rates.
     *
     * Weight(IP): 20
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database
     *
     * @summary Account Commission Rates (USER_DATA)
     * @param {AccountCommissionRequest} requestParameters Request parameters.
     *
     * @returns {Promise<AccountCommissionResponse>}
     * @memberof AccountApiInterface
     */
    accountCommission(
        requestParameters: AccountCommissionRequest
    ): Promise<WebsocketApiResponse<AccountCommissionResponse>>;

    /**
     * Query your current unfilled order count for all intervals.
     *
     * Weight(IP): 40
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Unfilled Order Count (USER_DATA)
     * @param {AccountRateLimitsOrdersRequest} requestParameters Request parameters.
     *
     * @returns {Promise<AccountRateLimitsOrdersResponse>}
     * @memberof AccountApiInterface
     */
    accountRateLimitsOrders(
        requestParameters?: AccountRateLimitsOrdersRequest
    ): Promise<WebsocketApiResponse<AccountRateLimitsOrdersResponse>>;

    /**
     * Query information about your account.
     *
     * Weight(IP): 20
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Memory => Database
     *
     * @summary Account information (USER_DATA)
     * @param {AccountStatusRequest} requestParameters Request parameters.
     *
     * @returns {Promise<AccountStatusResponse>}
     * @memberof AccountApiInterface
     */
    accountStatus(
        requestParameters?: AccountStatusRequest
    ): Promise<WebsocketApiResponse<AccountStatusResponse>>;

    /**
     * Query information about all your order lists, filtered by time range.
     *
     * Weight(IP): 20
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database
     *
     * Notes:
     * If `startTime` and/or `endTime` are specified, `fromId` is ignored.
     * Order lists are filtered by `transactionTime` of the last order list execution status update.
     * If `fromId` is specified, return order lists with order list ID >= `fromId`.
     * If no condition is specified, the most recent order lists are returned.
     * The time between `startTime` and `endTime` can't be longer than 24 hours.
     *
     * @summary Account order list history (USER_DATA)
     * @param {AllOrderListsRequest} requestParameters Request parameters.
     *
     * @returns {Promise<AllOrderListsResponse>}
     * @memberof AccountApiInterface
     */
    allOrderLists(
        requestParameters?: AllOrderListsRequest
    ): Promise<WebsocketApiResponse<AllOrderListsResponse>>;

    /**
     * Query information about all your orders – active, canceled, filled – filtered by time range.
     *
     * Weight(IP): 20
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database
     *
     * Notes:
     *
     * If `startTime` and/or `endTime` are specified, `orderId` is ignored.
     *
     * Orders are filtered by `time` of the last execution status update.
     *
     * If `orderId` is specified, return orders with order ID >= `orderId`.
     *
     * If no condition is specified, the most recent orders are returned.
     *
     * For some historical orders the `cummulativeQuoteQty` response field may be negative,
     * meaning the data is not available at this time.
     *
     * The time between `startTime` and `endTime` can't be longer than 24 hours.
     *
     * @summary Account order history (USER_DATA)
     * @param {AllOrdersRequest} requestParameters Request parameters.
     *
     * @returns {Promise<AllOrdersResponse>}
     * @memberof AccountApiInterface
     */
    allOrders(
        requestParameters: AllOrdersRequest
    ): Promise<WebsocketApiResponse<AllOrdersResponse>>;

    /**
     * Retrieves allocations resulting from SOR order placement.
     *
     * Weight(IP): 20
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database
     *
     * Supported parameter combinations:
     *
     * Parameters                                  | Response |
     * ------------------------------------------- | -------- |
     * `symbol`                                    | allocations from oldest to newest |
     * `symbol` + `startTime`                      | oldest allocations since `startTime` |
     * `symbol` + `endTime`                        | newest allocations until `endTime` |
     * `symbol` + `startTime` + `endTime`          | allocations within the time range |
     * `symbol` + `fromAllocationId`               | allocations by allocation ID |
     * `symbol` + `orderId`                        | allocations related to an order starting with oldest |
     * `symbol` + `orderId` + `fromAllocationId`   | allocations related to an order by allocation ID |
     *
     **Note:** The time between `startTime` and `endTime` can't be longer than 24 hours.
     *
     * @summary Account allocations (USER_DATA)
     * @param {MyAllocationsRequest} requestParameters Request parameters.
     *
     * @returns {Promise<MyAllocationsResponse>}
     * @memberof AccountApiInterface
     */
    myAllocations(
        requestParameters: MyAllocationsRequest
    ): Promise<WebsocketApiResponse<MyAllocationsResponse>>;

    /**
     * Retrieves the list of [filters](/products/spot/filters) relevant to an account on a given symbol. This is the only method
     * that shows if an account has [`MAX_ASSET`](/products/spot/filters#max_asset) filters applied to it.
     *
     * Weight(IP): 40
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Query Relevant Filters (USER_DATA)
     * @param {MyFiltersRequest} requestParameters Request parameters.
     *
     * @returns {Promise<MyFiltersResponse>}
     * @memberof AccountApiInterface
     */
    myFilters(
        requestParameters: MyFiltersRequest
    ): Promise<WebsocketApiResponse<MyFiltersResponse>>;

    /**
     * Displays the list of orders that were expired due to STP.
     *
     * These are the combinations supported:
     *
     * `symbol` + `preventedMatchId`
     * `symbol` + `orderId`
     * `symbol` + `orderId` + `fromPreventedMatchId` (`limit` will default to 500)
     * `symbol` + `orderId` + `fromPreventedMatchId` + `limit`
     *
     * Weight: Case                            | Weight
     * ----                            | -----
     * If `symbol` is invalid          | 2
     * Querying by `preventedMatchId`  | 2
     * Querying by `orderId`           | 20
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database
     *
     * @summary Account prevented matches (USER_DATA)
     * @param {MyPreventedMatchesRequest} requestParameters Request parameters.
     *
     * @returns {Promise<MyPreventedMatchesResponse>}
     * @memberof AccountApiInterface
     */
    myPreventedMatches(
        requestParameters: MyPreventedMatchesRequest
    ): Promise<WebsocketApiResponse<MyPreventedMatchesResponse>>;

    /**
     * Query information about all your trades, filtered by time range.
     *
     * Weight: Condition| Weight|
     * ---| ---
     * |Without orderId|20|
     * |With orderId|5|
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * Data Source: Memory => Database
     *
     * Notes:
     * - If `fromId` is specified, return trades with trade ID >= `fromId`.
     * - If `startTime` and/or `endTime` are specified, trades are filtered by execution time (`time`).
     * - `fromId` cannot be used together with `startTime` and `endTime`.
     * - If `orderId` is specified, only trades related to that order are returned.
     * - `startTime` and `endTime` cannot be used together with `orderId`.
     * - If no condition is specified, the most recent trades are returned.
     * - The time between `startTime` and `endTime` can't be longer than 24 hours.
     *
     * @summary Account trade history (USER_DATA)
     * @param {MyTradesRequest} requestParameters Request parameters.
     *
     * @returns {Promise<MyTradesResponse>}
     * @memberof AccountApiInterface
     */
    myTrades(requestParameters: MyTradesRequest): Promise<WebsocketApiResponse<MyTradesResponse>>;

    /**
     * Query execution status of all open order lists.
     *
     * If you need to continuously monitor order status updates, please consider using WebSocket Streams:
     *
     * `userDataStream.subscribe` if on an authenticated session
     * `userDataStream.subscribe.signature` if subscribing through signature subscription
     *
     * Weight(IP): 6
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Memory -> Database
     *
     * @summary Current open Order lists (USER_DATA)
     * @param {OpenOrderListsStatusRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OpenOrderListsStatusResponse>}
     * @memberof AccountApiInterface
     */
    openOrderListsStatus(
        requestParameters?: OpenOrderListsStatusRequest
    ): Promise<WebsocketApiResponse<OpenOrderListsStatusResponse>>;

    /**
     * Query execution status of all open orders.
     *
     * If you need to continuously monitor order status updates, please consider using WebSocket Streams:
     *
     * `userDataStream.subscribe` if on an authenticated session
     * `userDataStream.subscribe.signature` if subscribing through signature subscription
     *
     * Weight: | Parameter | Weight |
     * | --------- | ------ |
     * | `symbol`  |      6 |
     * | none      |     80 |
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * Data Source: Memory => Database
     *
     * @summary Current open orders (USER_DATA)
     * @param {OpenOrdersStatusRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OpenOrdersStatusResponse>}
     * @memberof AccountApiInterface
     */
    openOrdersStatus(
        requestParameters?: OpenOrdersStatusRequest
    ): Promise<WebsocketApiResponse<OpenOrdersStatusResponse>>;

    /**
     * Queries all amendments of a single order.
     *
     * Weight(IP): 4
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database
     *
     * @summary Query Order Amendments (USER_DATA)
     * @param {OrderAmendmentsRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OrderAmendmentsResponse>}
     * @memberof AccountApiInterface
     */
    orderAmendments(
        requestParameters: OrderAmendmentsRequest
    ): Promise<WebsocketApiResponse<OrderAmendmentsResponse>>;

    /**
     * Check execution status of an Order list.
     *
     * For execution status of individual orders, use `order.status`.
     *
     * Weight(IP): 4
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database
     *
     * Notes:
     *
     * `origClientOrderId` refers to `listClientOrderId` of the order list itself.
     *
     * If both `origClientOrderId` and `orderListId` parameters are specified,
     * only `origClientOrderId` is used and `orderListId` is ignored.
     *
     * @summary Query Order list (USER_DATA)
     * @param {OrderListStatusRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OrderListStatusResponse>}
     * @memberof AccountApiInterface
     */
    orderListStatus(
        requestParameters?: OrderListStatusRequest
    ): Promise<WebsocketApiResponse<OrderListStatusResponse>>;

    /**
     * Check execution status of an order.
     *
     * Weight(IP): 4
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Memory => Database
     *
     * Notes:
     *
     * If both `orderId` and `origClientOrderId` are provided, the `orderId` is searched first, then the `origClientOrderId` from that result is checked against that order. If both conditions are not met the request will be rejected.
     *
     * For some historical orders the `cummulativeQuoteQty` response field may be negative,
     * meaning the data is not available at this time.
     *
     * @summary Query order (USER_DATA)
     * @param {OrderStatusRequest} requestParameters Request parameters.
     *
     * @returns {Promise<OrderStatusResponse>}
     * @memberof AccountApiInterface
     */
    orderStatus(
        requestParameters: OrderStatusRequest
    ): Promise<WebsocketApiResponse<OrderStatusResponse>>;
}

/**
 * Request parameters for accountCommission operation in AccountApi.
 * @interface AccountCommissionRequest
 */
export interface AccountCommissionRequest {
    /**
     *
     * @type {string}
     * @memberof AccountApiAccountCommission
     */
    readonly symbol: string;

    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof AccountApiAccountCommission
     */
    readonly id?: string;
}

/**
 * Request parameters for accountRateLimitsOrders operation in AccountApi.
 * @interface AccountRateLimitsOrdersRequest
 */
export interface AccountRateLimitsOrdersRequest {
    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof AccountApiAccountRateLimitsOrders
     */
    readonly id?: string;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof AccountApiAccountRateLimitsOrders
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for accountStatus operation in AccountApi.
 * @interface AccountStatusRequest
 */
export interface AccountStatusRequest {
    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof AccountApiAccountStatus
     */
    readonly id?: string;

    /**
     * When set to `true`, emits only the non-zero balances of an account. Default value: `false`.
     * @type {boolean}
     * @memberof AccountApiAccountStatus
     */
    readonly omitZeroBalances?: boolean;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof AccountApiAccountStatus
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for allOrderLists operation in AccountApi.
 * @interface AllOrderListsRequest
 */
export interface AllOrderListsRequest {
    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof AccountApiAllOrderLists
     */
    readonly id?: string;

    /**
     * Order list ID to begin at
     * @type {number}
     * @memberof AccountApiAllOrderLists
     */
    readonly fromId?: number;

    /**
     * Timestamp in ms
     * @type {number | bigint}
     * @memberof AccountApiAllOrderLists
     */
    readonly startTime?: number | bigint;

    /**
     * Timestamp in ms
     * @type {number | bigint}
     * @memberof AccountApiAllOrderLists
     */
    readonly endTime?: number | bigint;

    /**
     * Default: 500; Maximum: 1000
     * @type {number}
     * @memberof AccountApiAllOrderLists
     */
    readonly limit?: number;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof AccountApiAllOrderLists
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for allOrders operation in AccountApi.
 * @interface AllOrdersRequest
 */
export interface AllOrdersRequest {
    /**
     *
     * @type {string}
     * @memberof AccountApiAllOrders
     */
    readonly symbol: string;

    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof AccountApiAllOrders
     */
    readonly id?: string;

    /**
     * Order ID to begin at
     * @type {number | bigint}
     * @memberof AccountApiAllOrders
     */
    readonly orderId?: number | bigint;

    /**
     * Timestamp in ms
     * @type {number | bigint}
     * @memberof AccountApiAllOrders
     */
    readonly startTime?: number | bigint;

    /**
     * Timestamp in ms
     * @type {number | bigint}
     * @memberof AccountApiAllOrders
     */
    readonly endTime?: number | bigint;

    /**
     * Default: 500; Maximum: 1000
     * @type {number}
     * @memberof AccountApiAllOrders
     */
    readonly limit?: number;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof AccountApiAllOrders
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for myAllocations operation in AccountApi.
 * @interface MyAllocationsRequest
 */
export interface MyAllocationsRequest {
    /**
     *
     * @type {string}
     * @memberof AccountApiMyAllocations
     */
    readonly symbol: string;

    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof AccountApiMyAllocations
     */
    readonly id?: string;

    /**
     * Timestamp in ms
     * @type {number | bigint}
     * @memberof AccountApiMyAllocations
     */
    readonly startTime?: number | bigint;

    /**
     * Timestamp in ms
     * @type {number | bigint}
     * @memberof AccountApiMyAllocations
     */
    readonly endTime?: number | bigint;

    /**
     * Allocation ID to begin at
     * @type {number}
     * @memberof AccountApiMyAllocations
     */
    readonly fromAllocationId?: number;

    /**
     * Default: 500; Maximum: 1000
     * @type {number}
     * @memberof AccountApiMyAllocations
     */
    readonly limit?: number;

    /**
     * Order ID
     * @type {number | bigint}
     * @memberof AccountApiMyAllocations
     */
    readonly orderId?: number | bigint;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof AccountApiMyAllocations
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for myFilters operation in AccountApi.
 * @interface MyFiltersRequest
 */
export interface MyFiltersRequest {
    /**
     *
     * @type {string}
     * @memberof AccountApiMyFilters
     */
    readonly symbol: string;

    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof AccountApiMyFilters
     */
    readonly id?: string;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof AccountApiMyFilters
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for myPreventedMatches operation in AccountApi.
 * @interface MyPreventedMatchesRequest
 */
export interface MyPreventedMatchesRequest {
    /**
     *
     * @type {string}
     * @memberof AccountApiMyPreventedMatches
     */
    readonly symbol: string;

    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof AccountApiMyPreventedMatches
     */
    readonly id?: string;

    /**
     * Prevented match ID
     * @type {number | bigint}
     * @memberof AccountApiMyPreventedMatches
     */
    readonly preventedMatchId?: number | bigint;

    /**
     * Order ID
     * @type {number | bigint}
     * @memberof AccountApiMyPreventedMatches
     */
    readonly orderId?: number | bigint;

    /**
     * Prevented match ID to begin at
     * @type {number | bigint}
     * @memberof AccountApiMyPreventedMatches
     */
    readonly fromPreventedMatchId?: number | bigint;

    /**
     * Default: 500; Maximum: 1000
     * @type {number}
     * @memberof AccountApiMyPreventedMatches
     */
    readonly limit?: number;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof AccountApiMyPreventedMatches
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for myTrades operation in AccountApi.
 * @interface MyTradesRequest
 */
export interface MyTradesRequest {
    /**
     *
     * @type {string}
     * @memberof AccountApiMyTrades
     */
    readonly symbol: string;

    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof AccountApiMyTrades
     */
    readonly id?: string;

    /**
     * This can only be used in combination with `symbol`.
     * @type {number | bigint}
     * @memberof AccountApiMyTrades
     */
    readonly orderId?: number | bigint;

    /**
     * Timestamp in ms
     * @type {number | bigint}
     * @memberof AccountApiMyTrades
     */
    readonly startTime?: number | bigint;

    /**
     * Timestamp in ms
     * @type {number | bigint}
     * @memberof AccountApiMyTrades
     */
    readonly endTime?: number | bigint;

    /**
     * First trade ID to query
     * @type {number}
     * @memberof AccountApiMyTrades
     */
    readonly fromId?: number;

    /**
     * Default: 500; Maximum: 1000
     * @type {number}
     * @memberof AccountApiMyTrades
     */
    readonly limit?: number;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof AccountApiMyTrades
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for openOrderListsStatus operation in AccountApi.
 * @interface OpenOrderListsStatusRequest
 */
export interface OpenOrderListsStatusRequest {
    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof AccountApiOpenOrderListsStatus
     */
    readonly id?: string;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof AccountApiOpenOrderListsStatus
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for openOrdersStatus operation in AccountApi.
 * @interface OpenOrdersStatusRequest
 */
export interface OpenOrdersStatusRequest {
    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof AccountApiOpenOrdersStatus
     */
    readonly id?: string;

    /**
     * If omitted, open orders for all symbols are returned
     * @type {string}
     * @memberof AccountApiOpenOrdersStatus
     */
    readonly symbol?: string;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof AccountApiOpenOrdersStatus
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for orderAmendments operation in AccountApi.
 * @interface OrderAmendmentsRequest
 */
export interface OrderAmendmentsRequest {
    /**
     *
     * @type {string}
     * @memberof AccountApiOrderAmendments
     */
    readonly symbol: string;

    /**
     * Order ID
     * @type {number | bigint}
     * @memberof AccountApiOrderAmendments
     */
    readonly orderId: number | bigint;

    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof AccountApiOrderAmendments
     */
    readonly id?: string;

    /**
     * Execution ID to begin at
     * @type {number | bigint}
     * @memberof AccountApiOrderAmendments
     */
    readonly fromExecutionId?: number | bigint;

    /**
     * Default: 500; Maximum: 1000
     * @type {number | bigint}
     * @memberof AccountApiOrderAmendments
     */
    readonly limit?: number | bigint;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof AccountApiOrderAmendments
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for orderListStatus operation in AccountApi.
 * @interface OrderListStatusRequest
 */
export interface OrderListStatusRequest {
    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof AccountApiOrderListStatus
     */
    readonly id?: string;

    /**
     * Query order list by `listClientOrderId`. `orderListId` or `origClientOrderId` must be provided.
     * @type {string}
     * @memberof AccountApiOrderListStatus
     */
    readonly origClientOrderId?: string;

    /**
     * Query order list by `orderListId`. `orderListId` or `origClientOrderId` must be provided.
     * @type {number}
     * @memberof AccountApiOrderListStatus
     */
    readonly orderListId?: number;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof AccountApiOrderListStatus
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for orderStatus operation in AccountApi.
 * @interface OrderStatusRequest
 */
export interface OrderStatusRequest {
    /**
     *
     * @type {string}
     * @memberof AccountApiOrderStatus
     */
    readonly symbol: string;

    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof AccountApiOrderStatus
     */
    readonly id?: string;

    /**
     * Lookup order by `orderId`
     * @type {number | bigint}
     * @memberof AccountApiOrderStatus
     */
    readonly orderId?: number | bigint;

    /**
     * Lookup order by `clientOrderId`
     * @type {string}
     * @memberof AccountApiOrderStatus
     */
    readonly origClientOrderId?: string;

    /**
     * Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof AccountApiOrderStatus
     */
    readonly recvWindow?: number;
}

/**
 * AccountApi - object-oriented interface
 * @class AccountApi
 * @extends {WebsocketAPIBase}
 */
export class AccountApi implements AccountApiInterface {
    private readonly websocketBase: WebsocketAPIBase;

    constructor(websocketBase: WebsocketAPIBase) {
        this.websocketBase = websocketBase;
    }

    /**
     * Get current account commission rates.
     *
     * Weight(IP): 20
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database
     *
     * @summary Account Commission Rates (USER_DATA)
     * @param {AccountCommissionRequest} requestParameters Request parameters.
     * @returns {Promise<AccountCommissionResponse>}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#account-commission Binance API Documentation}
     */
    public accountCommission(
        requestParameters: AccountCommissionRequest
    ): Promise<WebsocketApiResponse<AccountCommissionResponse>> {
        return this.websocketBase.sendMessage<AccountCommissionResponse>(
            '/account.commission'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Query your current unfilled order count for all intervals.
     *
     * Weight(IP): 40
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Unfilled Order Count (USER_DATA)
     * @param {AccountRateLimitsOrdersRequest} requestParameters Request parameters.
     * @returns {Promise<AccountRateLimitsOrdersResponse>}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#account-rate-limits-orders Binance API Documentation}
     */
    public accountRateLimitsOrders(
        requestParameters: AccountRateLimitsOrdersRequest = {}
    ): Promise<WebsocketApiResponse<AccountRateLimitsOrdersResponse>> {
        return this.websocketBase.sendMessage<AccountRateLimitsOrdersResponse>(
            '/account.rateLimits.orders'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Query information about your account.
     *
     * Weight(IP): 20
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Memory => Database
     *
     * @summary Account information (USER_DATA)
     * @param {AccountStatusRequest} requestParameters Request parameters.
     * @returns {Promise<AccountStatusResponse>}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#account-status Binance API Documentation}
     */
    public accountStatus(
        requestParameters: AccountStatusRequest = {}
    ): Promise<WebsocketApiResponse<AccountStatusResponse>> {
        return this.websocketBase.sendMessage<AccountStatusResponse>(
            '/account.status'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Query information about all your order lists, filtered by time range.
     *
     * Weight(IP): 20
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database
     *
     * Notes:
     * If `startTime` and/or `endTime` are specified, `fromId` is ignored.
     * Order lists are filtered by `transactionTime` of the last order list execution status update.
     * If `fromId` is specified, return order lists with order list ID >= `fromId`.
     * If no condition is specified, the most recent order lists are returned.
     * The time between `startTime` and `endTime` can't be longer than 24 hours.
     *
     * @summary Account order list history (USER_DATA)
     * @param {AllOrderListsRequest} requestParameters Request parameters.
     * @returns {Promise<AllOrderListsResponse>}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#all-order-lists Binance API Documentation}
     */
    public allOrderLists(
        requestParameters: AllOrderListsRequest = {}
    ): Promise<WebsocketApiResponse<AllOrderListsResponse>> {
        return this.websocketBase.sendMessage<AllOrderListsResponse>(
            '/allOrderLists'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Query information about all your orders – active, canceled, filled – filtered by time range.
     *
     * Weight(IP): 20
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database
     *
     * Notes:
     *
     * If `startTime` and/or `endTime` are specified, `orderId` is ignored.
     *
     * Orders are filtered by `time` of the last execution status update.
     *
     * If `orderId` is specified, return orders with order ID >= `orderId`.
     *
     * If no condition is specified, the most recent orders are returned.
     *
     * For some historical orders the `cummulativeQuoteQty` response field may be negative,
     * meaning the data is not available at this time.
     *
     * The time between `startTime` and `endTime` can't be longer than 24 hours.
     *
     * @summary Account order history (USER_DATA)
     * @param {AllOrdersRequest} requestParameters Request parameters.
     * @returns {Promise<AllOrdersResponse>}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#all-orders Binance API Documentation}
     */
    public allOrders(
        requestParameters: AllOrdersRequest
    ): Promise<WebsocketApiResponse<AllOrdersResponse>> {
        return this.websocketBase.sendMessage<AllOrdersResponse>(
            '/allOrders'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Retrieves allocations resulting from SOR order placement.
     *
     * Weight(IP): 20
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database
     *
     * Supported parameter combinations:
     *
     * Parameters                                  | Response |
     * ------------------------------------------- | -------- |
     * `symbol`                                    | allocations from oldest to newest |
     * `symbol` + `startTime`                      | oldest allocations since `startTime` |
     * `symbol` + `endTime`                        | newest allocations until `endTime` |
     * `symbol` + `startTime` + `endTime`          | allocations within the time range |
     * `symbol` + `fromAllocationId`               | allocations by allocation ID |
     * `symbol` + `orderId`                        | allocations related to an order starting with oldest |
     * `symbol` + `orderId` + `fromAllocationId`   | allocations related to an order by allocation ID |
     *
     **Note:** The time between `startTime` and `endTime` can't be longer than 24 hours.
     *
     * @summary Account allocations (USER_DATA)
     * @param {MyAllocationsRequest} requestParameters Request parameters.
     * @returns {Promise<MyAllocationsResponse>}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#my-allocations Binance API Documentation}
     */
    public myAllocations(
        requestParameters: MyAllocationsRequest
    ): Promise<WebsocketApiResponse<MyAllocationsResponse>> {
        return this.websocketBase.sendMessage<MyAllocationsResponse>(
            '/myAllocations'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Retrieves the list of [filters](/products/spot/filters) relevant to an account on a given symbol. This is the only method
     * that shows if an account has [`MAX_ASSET`](/products/spot/filters#max_asset) filters applied to it.
     *
     * Weight(IP): 40
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Query Relevant Filters (USER_DATA)
     * @param {MyFiltersRequest} requestParameters Request parameters.
     * @returns {Promise<MyFiltersResponse>}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#my-filters Binance API Documentation}
     */
    public myFilters(
        requestParameters: MyFiltersRequest
    ): Promise<WebsocketApiResponse<MyFiltersResponse>> {
        return this.websocketBase.sendMessage<MyFiltersResponse>(
            '/myFilters'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Displays the list of orders that were expired due to STP.
     *
     * These are the combinations supported:
     *
     * `symbol` + `preventedMatchId`
     * `symbol` + `orderId`
     * `symbol` + `orderId` + `fromPreventedMatchId` (`limit` will default to 500)
     * `symbol` + `orderId` + `fromPreventedMatchId` + `limit`
     *
     * Weight: Case                            | Weight
     * ----                            | -----
     * If `symbol` is invalid          | 2
     * Querying by `preventedMatchId`  | 2
     * Querying by `orderId`           | 20
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database
     *
     * @summary Account prevented matches (USER_DATA)
     * @param {MyPreventedMatchesRequest} requestParameters Request parameters.
     * @returns {Promise<MyPreventedMatchesResponse>}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#my-prevented-matches Binance API Documentation}
     */
    public myPreventedMatches(
        requestParameters: MyPreventedMatchesRequest
    ): Promise<WebsocketApiResponse<MyPreventedMatchesResponse>> {
        return this.websocketBase.sendMessage<MyPreventedMatchesResponse>(
            '/myPreventedMatches'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Query information about all your trades, filtered by time range.
     *
     * Weight: Condition| Weight|
     * ---| ---
     * |Without orderId|20|
     * |With orderId|5|
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * Data Source: Memory => Database
     *
     * Notes:
     * - If `fromId` is specified, return trades with trade ID >= `fromId`.
     * - If `startTime` and/or `endTime` are specified, trades are filtered by execution time (`time`).
     * - `fromId` cannot be used together with `startTime` and `endTime`.
     * - If `orderId` is specified, only trades related to that order are returned.
     * - `startTime` and `endTime` cannot be used together with `orderId`.
     * - If no condition is specified, the most recent trades are returned.
     * - The time between `startTime` and `endTime` can't be longer than 24 hours.
     *
     * @summary Account trade history (USER_DATA)
     * @param {MyTradesRequest} requestParameters Request parameters.
     * @returns {Promise<MyTradesResponse>}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#my-trades Binance API Documentation}
     */
    public myTrades(
        requestParameters: MyTradesRequest
    ): Promise<WebsocketApiResponse<MyTradesResponse>> {
        return this.websocketBase.sendMessage<MyTradesResponse>(
            '/myTrades'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Query execution status of all open order lists.
     *
     * If you need to continuously monitor order status updates, please consider using WebSocket Streams:
     *
     * `userDataStream.subscribe` if on an authenticated session
     * `userDataStream.subscribe.signature` if subscribing through signature subscription
     *
     * Weight(IP): 6
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Memory -> Database
     *
     * @summary Current open Order lists (USER_DATA)
     * @param {OpenOrderListsStatusRequest} requestParameters Request parameters.
     * @returns {Promise<OpenOrderListsStatusResponse>}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#open-order-lists-status Binance API Documentation}
     */
    public openOrderListsStatus(
        requestParameters: OpenOrderListsStatusRequest = {}
    ): Promise<WebsocketApiResponse<OpenOrderListsStatusResponse>> {
        return this.websocketBase.sendMessage<OpenOrderListsStatusResponse>(
            '/openOrderLists.status'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Query execution status of all open orders.
     *
     * If you need to continuously monitor order status updates, please consider using WebSocket Streams:
     *
     * `userDataStream.subscribe` if on an authenticated session
     * `userDataStream.subscribe.signature` if subscribing through signature subscription
     *
     * Weight: | Parameter | Weight |
     * | --------- | ------ |
     * | `symbol`  |      6 |
     * | none      |     80 |
     *
     * Security Type: USER_DATA
     *
     * Notes:
     * Data Source: Memory => Database
     *
     * @summary Current open orders (USER_DATA)
     * @param {OpenOrdersStatusRequest} requestParameters Request parameters.
     * @returns {Promise<OpenOrdersStatusResponse>}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#open-orders-status Binance API Documentation}
     */
    public openOrdersStatus(
        requestParameters: OpenOrdersStatusRequest = {}
    ): Promise<WebsocketApiResponse<OpenOrdersStatusResponse>> {
        return this.websocketBase.sendMessage<OpenOrdersStatusResponse>(
            '/openOrders.status'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Queries all amendments of a single order.
     *
     * Weight(IP): 4
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database
     *
     * @summary Query Order Amendments (USER_DATA)
     * @param {OrderAmendmentsRequest} requestParameters Request parameters.
     * @returns {Promise<OrderAmendmentsResponse>}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#order-amendments Binance API Documentation}
     */
    public orderAmendments(
        requestParameters: OrderAmendmentsRequest
    ): Promise<WebsocketApiResponse<OrderAmendmentsResponse>> {
        return this.websocketBase.sendMessage<OrderAmendmentsResponse>(
            '/order.amendments'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Check execution status of an Order list.
     *
     * For execution status of individual orders, use `order.status`.
     *
     * Weight(IP): 4
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database
     *
     * Notes:
     *
     * `origClientOrderId` refers to `listClientOrderId` of the order list itself.
     *
     * If both `origClientOrderId` and `orderListId` parameters are specified,
     * only `origClientOrderId` is used and `orderListId` is ignored.
     *
     * @summary Query Order list (USER_DATA)
     * @param {OrderListStatusRequest} requestParameters Request parameters.
     * @returns {Promise<OrderListStatusResponse>}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#order-list-status Binance API Documentation}
     */
    public orderListStatus(
        requestParameters: OrderListStatusRequest = {}
    ): Promise<WebsocketApiResponse<OrderListStatusResponse>> {
        return this.websocketBase.sendMessage<OrderListStatusResponse>(
            '/orderList.status'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }

    /**
     * Check execution status of an order.
     *
     * Weight(IP): 4
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Memory => Database
     *
     * Notes:
     *
     * If both `orderId` and `origClientOrderId` are provided, the `orderId` is searched first, then the `origClientOrderId` from that result is checked against that order. If both conditions are not met the request will be rejected.
     *
     * For some historical orders the `cummulativeQuoteQty` response field may be negative,
     * meaning the data is not available at this time.
     *
     * @summary Query order (USER_DATA)
     * @param {OrderStatusRequest} requestParameters Request parameters.
     * @returns {Promise<OrderStatusResponse>}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/account#order-status Binance API Documentation}
     */
    public orderStatus(
        requestParameters: OrderStatusRequest
    ): Promise<WebsocketApiResponse<OrderStatusResponse>> {
        return this.websocketBase.sendMessage<OrderStatusResponse>(
            '/order.status'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false }
        );
    }
}
